import { createSelector } from '@reduxjs/toolkit';

import { GlobalState } from '@/redux/store';

/**
 * The browser slice is fully populated on the server (see `redux/initialData.ts`, which uses
 * the UA-parsing helpers in `redux/modules/browserDetect.ts`) and reaches the client via
 * `window.__data`. Selectors here must only read state — importing the detection helpers
 * would pull browserslist, caniuse-lite, node-releases, and ua-parser-js (~112 KB parsed)
 * into the initial client bundle.
 */

export type BrowserSlice = {
    acceptLanguage?: string;
    viewerCountry?: string;
    deviceType: string;
    ipAddress?: string;
    isOutdated: boolean;
    majorVersion: number;
    microsoftBrowser: boolean;
    mobile: string;
    name: string;
    os: string;
    osVersion: string;
    userAgentString: string;
};

export const defaultBrowserSlice: BrowserSlice = {
    acceptLanguage: '',
    viewerCountry: '',
    deviceType: 'computer',
    ipAddress: '',
    isOutdated: false,
    majorVersion: 0,
    microsoftBrowser: false,
    mobile: '',
    name: '',
    os: '',
    osVersion: '',
    userAgentString: '',
};

export const ClientDeviceTypes = {
    COMPUTER: 'computer',
    CONSOLE: 'console',
    EMBEDDED: 'embedded',
    MOBILE: 'mobile',
    PHONE: 'phone',
    SMARTTV: 'smarttv',
    TABLET: 'tablet',
    WEARABLE: 'wearable',
};

export default function reducer(state: BrowserSlice = defaultBrowserSlice, action: any = {}): BrowserSlice {
    switch (action.type) {
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;

export const getUiBrowser = createSelector(stateSelector, (state) => state.browser);

export const getViewerCloudFrontCountry = createSelector(getUiBrowser, ({ viewerCountry }) => viewerCountry ?? '');

export const isUiMobile = createSelector(getUiBrowser, ({ mobile }) => mobile);

export const getClientIpAddress = createSelector(getUiBrowser, ({ ipAddress }) => ipAddress);

export const getMobileBrowserOS = createSelector(getUiBrowser, (uiBrowser) => {
    return uiBrowser.mobile ? uiBrowser.os : '';
});

export const isUiMicrosoftBrowser = createSelector(getUiBrowser, ({ microsoftBrowser }) => microsoftBrowser);

export const isUiTablet = createSelector(isUiMobile, (mobile) => {
    return mobile === ClientDeviceTypes.TABLET;
});

export const getUserAgent = createSelector(getUiBrowser, ({ userAgentString }) => userAgentString);

export const isE2ETest = createSelector(getUserAgent, (userAgent: string) => /LA[- ]E2E/.test(userAgent));

export const getIsBrowserOutdated = createSelector(getUiBrowser, ({ isOutdated }) => isOutdated);

export const getBrowserName = createSelector(getUiBrowser, ({ name }) => name);

export const getBrowserMajorVersion = createSelector(getUiBrowser, ({ majorVersion }) => majorVersion);
